<script setup lang="ts">
import { onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import PublicSiteFooter from '@/components/publicSite/PublicSiteFooter.vue'
import PublicSiteHeader from '@/components/publicSite/PublicSiteHeader.vue'
import { $api } from '@/utils/api'

definePage({
  meta: {
    layout: 'blank',
    public: true,
  },
})

interface ChangelogEntry {
  id: number
  entry_date: string
  title: string
  body: string
  version?: string | null
}

const { t, locale } = useI18n({ useScope: 'global' })

const entries = ref<ChangelogEntry[]>([])
const isLoading = ref(true)

const fetchEntries = async () => {
  isLoading.value = true
  try {
    const data = await $api<{ entries: ChangelogEntry[] }>(`/changelog?locale=${locale.value}`)
    entries.value = data?.entries ?? []
  }
  catch {
    entries.value = []
  }
  finally {
    isLoading.value = false
  }
}

const formatDate = (value: string) => new Intl.DateTimeFormat(locale.value || 'it', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
}).format(new Date(`${value}T00:00:00`))

onMounted(fetchEntries)
watch(locale, fetchEntries)
</script>

<template>
  <div class="changelog-premium">
    <PublicSiteHeader />

    <header class="changelog-hero">
      <div class="container text-center">
        <h1 class="text-h1 hero-title mb-4">
          {{ t('changelog_public_title') }}
        </h1>
        <p class="text-h6 hero-description mx-auto">
          {{ t('changelog_public_lead') }}
        </p>
      </div>
    </header>

    <section class="entries-section">
      <div class="container entries-container">
        <p
          v-if="!isLoading && entries.length === 0"
          class="empty-state"
        >
          {{ t('changelog_public_empty') }}
        </p>

        <template
          v-for="(entry, index) in entries"
          :key="entry.id"
        >
          <h2
            v-if="entry.version && entry.version !== entries[index - 1]?.version"
            class="version-heading"
          >
            v{{ entry.version }}
          </h2>

          <article class="entry-card">
            <time
              :datetime="entry.entry_date"
              class="entry-date"
            >
              {{ formatDate(entry.entry_date) }}
            </time>

            <h3 class="entry-title">
              {{ entry.title }}
            </h3>

            <div
              class="entry-body"
              v-html="entry.body"
            />
          </article>
        </template>
      </div>
    </section>

    <PublicSiteFooter />
  </div>
</template>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;600;800&display=swap');

.changelog-premium {
  font-family: 'Outfit', sans-serif;
  background-color: #0F172A;
  color: #F1F5F9;
  min-height: 100vh;
}

.container { max-width: 1600px; margin: 0 auto; padding: 0 40px; }

.changelog-hero { padding: 100px 0 60px; }
.hero-title { color: #FFFFFF; }
.hero-description { color: #E5EEF9; max-width: 640px; }

.entries-section { padding: 0 0 100px; }
.entries-container { max-width: 800px; }

.empty-state { color: #E5EEF9; font-size: 16px; }

.entry-card {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 28px 32px;
  margin-bottom: 24px;
}
.version-heading {
  font-size: 15px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #A5B4FC;
  margin: 56px 0 18px;
  padding-bottom: 10px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}
.version-heading:first-child { margin-top: 0; }
.entry-date { display: block; font-size: 13px; color: #A5B4FC; font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em; margin-bottom: 10px; }
.entry-title { font-size: 22px; font-weight: 700; color: #FFFFFF; margin: 0 0 12px; }
.entry-body { color: #E5EEF9; font-size: 15px; line-height: 1.7; }
.entry-body :deep(p) { margin: 0 0 10px; }
.entry-body :deep(ul) { padding-left: 20px; margin: 0 0 10px; }
.entry-body :deep(h2),
.entry-body :deep(h3) { color: #FFFFFF; font-size: 17px; font-weight: 700; margin: 16px 0 8px; }
.entry-body :deep(a) { color: #A5B4FC; }

@media (max-width: 600px) {
  .container { padding: 0 20px; }
  .entry-card { padding: 22px 20px; }
}
</style>
